import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";

import {
	TextField,
	InputAdornment,
	Select,
	MenuItem,
	Button,
	Switch,
	FormControlLabel
} from "@mui/material";
import { Pagination } from "@mui/material";
import { Search } from "@mui/icons-material";
import { THEME_MAP } from "../theme";

import { useUsers, useUserUtils } from "../actions/users";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export function Users() {
	const history = useHistory();
	const { search } = useLocation();
	const { users, endOfResults, loading, totalCount } = useUsers();
	const { getNext, setUsersQuery } = useUserUtils();

	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [currentPage, setCurrentPage] = useState(1);
	const [pendingPage, setPendingPage] = useState(null);
	const [pendingLast, setPendingLast] = useState(false);
	const [expandedUserId, setExpandedUserId] = useState(null);

	const canPaginate = !search;
	const totalPages = Math.max(
		1,
		Math.ceil(((totalCount ?? users?.length ?? 0) || 0) / pageSize)
	);
	const paginationCount = totalPages;
	const paginationPage = Math.min(Math.max(1, currentPage), paginationCount);

	const handlePageChange = useCallback((_, value) => {
		setPendingLast(false);
		setPendingPage(null);

		const requiredCount = (value - 1) * pageSize + 1;
		const beyondLoaded = requiredCount > (users?.length || 0);
		const aimingForEnd = value >= paginationCount;

		if (aimingForEnd && !endOfResults) {
			setPendingLast(true);
			if (!loading) {
				getNext();
			}
			return;
		}

		if (beyondLoaded && !endOfResults) {
			setPendingPage(value);
			if (!loading) {
				getNext();
			}
			return;
		}

		setCurrentPage(Math.min(value, paginationCount));
	}, [endOfResults, getNext, loading, pageSize, paginationCount, users?.length]);

	const handlePageSizeChange = useCallback((event) => {
		const nextSize = Number(event.target.value) || DEFAULT_PAGE_SIZE;
		setPageSize(nextSize);
	}, []);

	// // The 'handleSearch' function makes the dependencies of useEffect Hook (at line 41) change on every render. To fix this, wrap the 'handleSearch' definition into its own useCallback() Hook
	const handleSearch = useCallback(
		(e) => {
			setUsersQuery(e.target.value);
			history.push({ search: `?q=${e.target.value}` });
			setCurrentPage(1);
			setPendingPage(null);
			setPendingLast(false);
		},
		[history, setUsersQuery]
	);

	useEffect(() => {
		let hasQuery = search.match(/q=([\w\W]+?)(&|$)/);
		if (hasQuery) {
			handleSearch({ target: { value: hasQuery[1] } });
		} else {
			if (!users?.length) getNext();
		}
	}, [getNext, handleSearch, search, users.length]);

	useEffect(() => {
		if (currentPage > paginationCount) {
			setCurrentPage(paginationCount);
		}
	}, [currentPage, paginationCount]);

	const paginatedUsers = useMemo(() => {
		const start = (paginationPage - 1) * pageSize;
		return (users || []).slice(start, start + pageSize);
	}, [paginationPage, pageSize, users]);

	const pageStartIndex = paginatedUsers.length
		? (paginationPage - 1) * pageSize + 1
		: 0;
	const pageEndIndex = paginatedUsers.length
		? Math.min(
			pageStartIndex + pageSize - 1,
			totalCount ?? (paginationPage - 1) * pageSize + paginatedUsers.length
		)
		: 0;

	const pageSummaryText = useMemo(() => {
		if (loading && (users?.length || 0) === 0) {
			return "Loading users...";
		}
		if ((users?.length || 0) === 0) {
			return "No users to display";
		}
		if ((pendingLast || pendingPage !== null) && !endOfResults) {
			return "Fetching more users...";
		}
		const knownTotal = totalCount ?? users.length;
		const suffix = totalCount === null && !endOfResults ? `${users.length}+` : knownTotal;
		return `${pageStartIndex}-${pageEndIndex} of ${suffix} users`;
	}, [endOfResults, loading, pageEndIndex, pageStartIndex, pendingLast, pendingPage, totalCount, users?.length]);

	useEffect(() => {
		if (!canPaginate || loading) {
			return;
		}

		if (pendingLast) {
			if (endOfResults) {
				setCurrentPage(Math.max(1, totalPages));
				setPendingLast(false);
			} else {
				getNext();
			}
		}
	}, [canPaginate, endOfResults, getNext, loading, pendingLast, totalPages]);

	useEffect(() => {
		if (!canPaginate || loading || pendingLast || pendingPage === null) {
			return;
		}

		const requiredCount = (pendingPage - 1) * pageSize + 1;
		if ((users?.length || 0) >= requiredCount || endOfResults) {
			setCurrentPage(Math.min(pendingPage, totalPages));
			setPendingPage(null);
			return;
		}

		getNext();
	}, [canPaginate, endOfResults, getNext, loading, pageSize, pendingLast, pendingPage, totalPages, users?.length]);

	useEffect(() => {
		setCurrentPage(1);
		setPendingPage(null);
		setPendingLast(false);
	}, [pageSize, totalCount]);

	useEffect(() => {
		if (!canPaginate || loading || endOfResults) {
			return;
		}

		const requiredCount = paginationPage * pageSize;
		if ((users?.length || 0) < requiredCount) {
			getNext();
		}
	}, [canPaginate, endOfResults, getNext, loading, pageSize, paginationPage, users?.length]);

	return (
		<Container>
			<div className="filters">
				<TextField
					className="input"
					placeholder="Filter"
					variant="outlined"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search>Filled</Search>
							</InputAdornment>
						)
					}}
					onChange={handleSearch}
				/>
			</div>
			<div className="results-meta">
				<span>{pageSummaryText}</span>
				{loading ? <span className="loading-indicator">Loading...</span> : null}
			</div>

			<div className="users-container">
				{paginatedUsers.map((snapshot) => (
					<UserListItem
						key={snapshot.id}
						userSnapshot={snapshot}
						expanded={expandedUserId === snapshot.id}
						onToggle={() => setExpandedUserId(expandedUserId === snapshot.id ? null : snapshot.id)}
					/>
				))}
				{loading && paginatedUsers.length === 0 && (users?.length || 0) > 0 && (
					<div className="no-results">Loading page...</div>
				)}
				{(users?.length || 0) === 0 && !loading && (
					<div className="no-results">No users found</div>
				)}
			</div>

			<div className="pagination-footer">
				<div className="rows-per-page">
					<span className="label">Rows per page</span>
						<Select size="small" value={pageSize} onChange={handlePageSizeChange} disabled={!canPaginate}>
						{PAGE_SIZE_OPTIONS.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</div>
				<div className="page-controls">
					<span className="page-summary">{pageSummaryText}</span>
					<Pagination
						count={paginationCount}
						page={paginationPage}
						onChange={handlePageChange}
						color="primary"
						shape="rounded"
						showFirstButton
						showLastButton
						siblingCount={1}
						boundaryCount={1}
							disabled={!canPaginate || (loading && (users?.length || 0) === 0)}
					/>
				</div>
			</div>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;

	overflow: auto;

	padding: 24px;

	.input {
		background: #fff;
	}

	.filters {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.users-container {
		width: 100%;
		min-height: 100px;
		border-radius: 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-200"]};

		margin-bottom: 16px;

		max-height: calc(100% - 125px);
		overflow-y: scroll;
	}

	.results-meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		color: ${THEME_MAP["color-basic-600"]};
	}

	.pagination-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px 0;
		border-top: 1px solid ${THEME_MAP["color-basic-300"]};
	}

	.rows-per-page {
		display: flex;
		align-items: center;
		gap: 8px;
		color: ${THEME_MAP["color-basic-700"]};
		font-weight: 600;
	}

	.page-controls {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		justify-content: flex-end;
		min-width: 260px;
		flex-wrap: wrap;
	}

	.page-summary {
		color: ${THEME_MAP["color-basic-700"]};
		white-space: nowrap;
	}

	.loading-indicator {
		color: ${THEME_MAP["color-primary-600"]};
		font-weight: 600;
		margin-left: 8px;
	}

	.no-results {
		padding: 12px 16px;
		color: ${THEME_MAP["color-basic-700"]};
	}

	.user-row {
		display: flex;
		flex-direction: column;
		border-bottom: 1px solid ${THEME_MAP["color-basic-300"]};
		background: #fff;
		position: relative;
		cursor: pointer;

		.name-email {
			display: flex;
			flex-direction: column;
			gap: 4px;
			word-break: break-word;
			color: ${THEME_MAP["color-basic-800"]};

			.email {
				font-weight: 600;
			}
			.name {
				color: ${THEME_MAP["color-basic-600"]};
				font-size: 0.95rem;
			}
		}

		.meta {
			display: flex;
			flex-direction: column;
			gap: 4px;
			color: ${THEME_MAP["color-basic-700"]};
			font-size: 0.95rem;
		}

		.meta.pills {
			flex-direction: row;
			align-items: center;
			flex-wrap: wrap;
			gap: 6px;
		}

		.pill {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			padding: 4px 10px;
			border-radius: 999px;
			font-weight: 700;
			font-size: 0.85rem;
			color: ${THEME_MAP["color-primary-700"]};
			background: ${THEME_MAP["color-primary-transparent-200"]};
		}

		.pill.chip {
			padding: 3px 8px;
			font-size: 0.8rem;
			font-weight: 600;
		}

		.pill.male {
			color: ${THEME_MAP["color-primary-800"]};
			background: ${THEME_MAP["color-primary-transparent-200"]};
		}

		.pill.female {
			color: ${THEME_MAP["color-danger-700"]};
			background: ${THEME_MAP["color-danger-transparent-200"]};
		}

		.pill.other {
			color: ${THEME_MAP["color-info-700"]};
			background: ${THEME_MAP["color-info-transparent-200"]};
		}

		.pill.single {
			color: ${THEME_MAP["color-warning-800"]};
			background: ${THEME_MAP["color-warning-transparent-200"]};
		}

		.pill.taken {
			color: ${THEME_MAP["color-success-800"]};
			background: ${THEME_MAP["color-success-transparent-200"]};
		}

		.pill.relationship-other {
			color: ${THEME_MAP["color-basic-800"]};
			background: ${THEME_MAP["color-basic-300"]};
		}

		.pill.muted {
			color: ${THEME_MAP["color-basic-700"]};
			background: ${THEME_MAP["color-basic-300"]};
		}

		.pill.soft {
			color: ${THEME_MAP["color-primary-700"]};
			background: ${THEME_MAP["color-primary-transparent-100"]};
			font-weight: 600;
		}

		.subtext {
			color: ${THEME_MAP["color-basic-500"]};
			font-size: 0.85rem;
		}

		.actions {
			display: flex;
			justify-content: flex-end;
		}

		.meta.status {
			align-items: flex-end;
		}

		.row-main {
			display: grid;
			grid-template-columns: 1.4fr 1.2fr 1.1fr 0.7fr;
			align-items: center;
			gap: 12px;
			padding: 12px 16px;
			transition: background 0.12s ease;

			.user-row:hover & {
				background: ${THEME_MAP["color-primary-transparent-100"]};
			}
		}

		.expand-panel {
			padding: 12px 16px 16px;
			background: ${THEME_MAP["color-basic-200"]};
			border-top: 1px solid ${THEME_MAP["color-basic-300"]};
		}

		.expand-grid {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
			gap: 12px;
			margin-bottom: 12px;
		}

		.expand-actions {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			flex-wrap: wrap;
		}

		.expand-actions .error-text {
			color: ${THEME_MAP["color-danger-700"]};
			font-weight: 600;
		}

		.expand-actions .buttons {
			display: flex;
			align-items: center;
			gap: 8px;
		}
	}

	.users-container .user-row:last-child {
		border-bottom: none;
	}
`;

function UserListItem({ userSnapshot, expanded, onToggle }) {
	const [currentRef] = useState(userSnapshot.ref);
	const [user, setUser] = useState({
		id: userSnapshot?.id,
		...(userSnapshot?.data() || {})
	});
	const [form, setForm] = useState({
		fullName: "",
		gender: "",
		relationship: "",
		birthdate: "",
		isAdmin: false
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (currentRef) {
			return currentRef.onSnapshot((snap) => {
				if (snap) {
					const data = snap.data();
					setUser({ id: snap.id, ...data });
				}
			});
		}
	}, [currentRef]);

	const email = user?.email || "(anonymous)";
	const fullName = user?.fullName || user?.full_name || user?.name || [
		user?.firstName || user?.first_name || "",
		user?.lastName || user?.last_name || ""
	].filter(Boolean).join(" ");
	const gender = user?.gender || "";
	const relationship = user?.relationshipStatus || "";
	const { ageText } = formatBirthdate(
		user?.birthday || user?.birthdate || user?.birthday_at
	);
	const genderClass = getGenderClass(gender);
	const relationshipClass = getRelationshipClass(relationship);
	const isAdmin = !!user?.isAdmin;
	const visibility = user?.visibility || "";
	const primaryRole = extractPrimaryRole(user?.roles);
	const rolesCount = user?.roles ? Object.keys(user.roles).length : 0;

	const handleFieldChange = useCallback((key) => (event) => {
		setForm((prev) => ({ ...prev, [key]: event.target.value }));
	}, []);

	const handleAdminToggle = useCallback((event) => {
		setForm((prev) => ({ ...prev, isAdmin: event.target.checked }));
	}, []);

	const handleSave = useCallback(async (event) => {
		event.stopPropagation();
		if (!currentRef) return;
		setSaving(true);
		setError("");
		try {
			const payload = {
				fullName: form.fullName || null,
				gender: form.gender || null,
				relationshipStatus: form.relationship || null,
				isAdmin: !!form.isAdmin
			};

			payload.birthday = form.birthdate ? dateInputToISOString(form.birthdate) : null;

			await currentRef.update(payload);
		} catch (err) {
			setError(err?.message || "Failed to save user");
		} finally {
			setSaving(false);
		}
	}, [currentRef, form.birthdate, form.fullName, form.gender, form.relationship, form.isAdmin]);

	const handleCancel = useCallback((event) => {
		event.stopPropagation();
		const birthDateValue = user?.birthday || user?.birthdate || user?.birthday_at;
		setForm({
			fullName,
			gender,
			relationship,
			birthdate: formatDateInput(birthDateValue),
			isAdmin
		});
		setError("");
	}, [fullName, gender, relationship, isAdmin, user?.birthday, user?.birthdate, user?.birthday_at]);

	useEffect(() => {
		const birthDateValue = user?.birthday || user?.birthdate || user?.birthday_at;
		setForm({
			fullName,
			gender,
			relationship,
			birthdate: formatDateInput(birthDateValue),
			isAdmin
		});
		setError("");
	}, [fullName, gender, relationship, isAdmin, user?.birthday, user?.birthdate, user?.birthday_at, user?.id]);

	return (
		<div
			className={`user-row ${expanded ? "expanded" : ""}`}
			onClick={onToggle}
			onKeyDown={(e) => {
				const target = e.target;
				const tag = (target?.tagName || "").toLowerCase();
				if (tag === "input" || tag === "select" || tag === "textarea" || tag === "button" || target?.isContentEditable) {
					return;
				}
				if (e.key === "Enter") {
					e.preventDefault();
					onToggle();
				}
			}}
			role="button"
			tabIndex={0}
		>
			<div className="row-main">
				<div className="name-email">
					<span className="email">{email}</span>
					{fullName ? <span className="name">{fullName}</span> : null}
				</div>
				<div className="meta pills">
					{gender ? <span className={`pill chip ${genderClass}`}>{gender}</span> : null}
					{relationship ? <span className={`pill chip ${relationshipClass}`}>{relationship}</span> : null}
					{ageText ? <span className="pill chip muted">{ageText}</span> : null}
				</div>
				<div className="meta">
					{rolesCount ? <span>{`${rolesCount} role${rolesCount > 1 ? "s" : ""}`}</span> : null}
					{primaryRole ? <span className="subtext">{primaryRole}</span> : null}
				</div>
				<div className="meta status">
					{isAdmin ? <span className="pill">Admin</span> : null}
				</div>
			</div>
			{expanded ? (
				<div className="expand-panel" onClick={(e) => e.stopPropagation()}>
					<div className="expand-grid">
						<TextField
							label="Full name"
							size="small"
							value={form.fullName}
							onChange={handleFieldChange("fullName")}
							fullWidth
						/>
						<TextField
							select
							label="Gender"
							size="small"
							value={form.gender}
							onChange={handleFieldChange("gender")}
							fullWidth
						>
							<MenuItem value="Male">Male</MenuItem>
							<MenuItem value="Female">Female</MenuItem>
							<MenuItem value="Other">Other</MenuItem>
						</TextField>
						<TextField
							select
							label="Relationship"
							size="small"
							value={form.relationship}
							onChange={handleFieldChange("relationship")}
							fullWidth
						>
							<MenuItem value="Single">Single</MenuItem>
							<MenuItem value="Taken">Taken</MenuItem>
						</TextField>
						<TextField
							label="Birthday"
							type="date"
							size="small"
							value={form.birthdate}
							onChange={handleFieldChange("birthdate")}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
						<FormControlLabel
							control={
								<Switch
									checked={!!form.isAdmin}
									onChange={handleAdminToggle}
									color="primary"
									inputProps={{ "aria-label": "admin toggle" }}
								/>
							}
							label="Admin"
						/>
						<TextField
							label="Visibility"
							value={visibility || "—"}
							size="small"
							InputProps={{ readOnly: true }}
							disabled
						/>
					</div>
					<div className="expand-actions">
						{error ? <span className="error-text">{error}</span> : <span />}
						<div className="buttons">
							<Button variant="text" size="small" onClick={handleCancel} disabled={saving}>
								Cancel
							</Button>
							<Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
								{saving ? "Saving..." : "Save"}
							</Button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

function formatBirthdate(value) {
	if (!value) return { ageText: "" };
	try {
		const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
		if (Number.isNaN(date.getTime())) return { ageText: "" };
		const now = new Date();
		let age = now.getFullYear() - date.getFullYear();
		const hasHadBirthday =
			now.getMonth() > date.getMonth() ||
			(now.getMonth() === date.getMonth() && now.getDate() >= date.getDate());
		if (!hasHadBirthday) age -= 1;
		return {
			ageText: age >= 0 && age < 130 ? `${age} yo` : ""
		};
	} catch (_err) {
		return { ageText: "" };
	}
}

function extractPrimaryRole(roles) {
	if (!roles || typeof roles !== "object") return "";
	const entries = Object.values(roles);
	if (!entries.length) return "";
	const first = entries[0];
	const bar = first.barName || first.bar_name || "";
	const role = first.role || "";
	return [role, bar].filter(Boolean).join(" @ ");
}

function formatDateInput(value) {
	if (!value) return "";
	try {
		const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
		if (Number.isNaN(date.getTime())) return "";
		const year = date.getFullYear();
		const month = `${date.getMonth() + 1}`.padStart(2, "0");
		const day = `${date.getDate()}`.padStart(2, "0");
		return `${year}-${month}-${day}`;
	} catch (_err) {
		return "";
	}
}

function dateInputToISOString(dateStr) {
	if (!dateStr || typeof dateStr !== "string") return null;
	const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!match) return null;
	const [, y, m, d] = match;
	return `${y}-${m}-${d}T05:00:00.000Z`;
}

function getGenderClass(value) {
	const normalized = (value || "").toLowerCase();
	if (normalized === "male") return "male";
	if (normalized === "female") return "female";
	if (normalized) return "other";
	return "";
}

function getRelationshipClass(value) {
	const normalized = (value || "").toLowerCase();
	if (normalized === "single") return "single";
	if (normalized === "taken" || normalized === "in a relationship") return "taken";
	if (normalized) return "relationship-other";
	return "";
}

